<template>
	<section class="card">
		<header class="card-hdr">
			<div class="title-group">
				<h3>{{ label }}</h3>
				<div
					v-if="currentRange !== 'custom'"
					class="live-status"
				>
					<span
						class="live-indicator"
						:class="{ 'is-delay': isDelay }"
					>
						<span class="dot"></span> {{ isDelay ? "DELAY" : "LIVE" }}
					</span>
					<span
						v-if="lastUpdate"
						class="last-update"
					>
						Ultimo aggiornamento {{ lastUpdate }}
					</span>
				</div>
			</div>

			<div class="range-buttons">
				<button
					@click="fetchData('1h')"
					:class="{ active: currentRange === '1h' }"
					data-range="1h"
				>
					1h
				</button>
				<button
					@click="fetchData('24h')"
					:class="{ active: currentRange === '24h' }"
					data-range="24h"
				>
					24h
				</button>
				<button
					@click="fetchData('7d')"
					:class="{ active: currentRange === '7d' }"
					data-range="7d"
				>
					<span class="full-label">7 giorni</span>
					<span class="short-label">7g</span>
				</button>
				<button
					@click="fetchData('30d')"
					:class="{ active: currentRange === '30d' }"
					data-range="30d"
				>
					<span class="full-label">30 giorni</span>
					<span class="short-label">30g</span>
				</button>
				<button
					@click="openDayPicker"
					:class="{ active: currentRange === 'custom' }"
					data-range="custom"
				>
					{{ dayButtonText }}
				</button>
			</div>
		</header>

		<div class="card-body chart-container-rel" @wheel="handleWheel">
			<canvas ref="canvas"></canvas>
			<div class="zoom-hint-overlay" :class="{ show: showZoomHint }">
				<div class="zoom-hint-content">
					Usa <kbd>Alt</kbd> + rotella per zoomare
				</div>
			</div>
		</div>

		<div
			v-if="categories.length"
			class="state-legend"
		>
			<div
				v-for="cat in categories"
				:key="cat"
				class="state-legend-item"
			>
				<span
					class="color-dot"
					:style="{ backgroundColor: getStateColor(cat) }"
				></span>
				<span class="state-label">{{ cat }}</span>
			</div>
		</div>

		<DayPickerPopup
			v-if="showDayPicker"
			:isOpen="showDayPicker"
			@close="closeDayPicker"
			@apply="applyDaySelection"
		/>
	</section>
</template>

<script setup>
	import axios from "@/axiosInstance";
	import DayPickerPopup from "@/components/DayPickerPopup.vue";
	import { chartTimeConfig, isoToTimestamp } from "@/utils/chartTime";
	import Chart from "chart.js/auto";
	import "chartjs-adapter-luxon";
	import zoomPlugin from "chartjs-plugin-zoom";
	import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";
	import { useToast } from "vue-toast-notification";

	const toast = useToast();
	Chart.register(zoomPlugin);


	const props = defineProps({
		entity: String,
		deviceId: [String, Number],
		initialRange: { type: String, default: "24h" },
		initialSelectedDay: { type: Object, default: null },
	});

	const emit = defineEmits(["update:range", "update:selectedDay"]);

	const canvas = ref(null);
	const chartInstance = shallowRef(null);
	const currentRange = ref(props.initialRange);
	const showDayPicker = ref(false);
	const showZoomHint = ref(false);
	let hintTimeout = null;

	function handleWheel(e) {
		if (!e.altKey) {
			showZoomHint.value = true;
			if (hintTimeout) clearTimeout(hintTimeout);
			hintTimeout = setTimeout(() => {
				showZoomHint.value = false;
			}, 2000);
		} else {
			showZoomHint.value = false;
		}
	}
	const selectedDay = ref(props.initialSelectedDay);
	const categories = ref([]);
	const isDelay = ref(true);

	// 🎨 Mappa colori stati
	const STATE_COLORS = {
		charging: "#4caf50",
		discharging: "#ff5252",
		idle: "#9e9e9e",

		"Grid connected": "#2196f3",
		"Grid disconnected": "#ff9800",
		Normal: "#4caf50",
		Fault: "#f44336",

		"Self Use": "#00bcd4",
		Lithium: "#9c27b0",
		Backup: "#ffc107",

		unavailable: "#757575",
		unknown: "#455a64",

		on: "#4caf50",
		off: "#f44336",
	};

	function getStateColor(value) {
		return STATE_COLORS[value] || "#90caf9";
	}

	const STATE_INDEX = {
		charging: 2,
		idle: 1,
		discharging: 0,
	};

	const label = computed(() => props.entity);

	const dayButtonText = computed(() => {
		if (!selectedDay.value) return "Personalizzato";

		const { start, end } = selectedDay.value;

		if (!(start instanceof Date) || isNaN(start)) {
			return "Personalizzato";
		}

		const from = start.toLocaleDateString("it-IT", {
			day: "2-digit",
			month: "2-digit",
		});

		const to = end?.toLocaleDateString("it-IT", {
			day: "2-digit",
			month: "2-digit",
		});

		return to ? `${from} → ${to}` : `${from} → Oggi`;
	});

	const refreshInterval = ref(null);

	// Funzione per avviare il polling
	function startPolling() {
		// Pulisci timer esistenti per sicurezza
		stopPolling();

		// 120000 ms = ogni 2 minuti
		refreshInterval.value = setInterval(() => {
			// Aggiorniamo solo se NON siamo in modalità "custom"
			// o se il "custom" include la data odierna
			if (currentRange.value !== "custom" || !selectedDay.value?.end) {
				console.log(`🔄 Auto-refresh per ${props.entity}...`);
				fetchData(currentRange.value, selectedDay.value, true);
			}
		}, 120000);
	}

	function stopPolling() {
		if (refreshInterval.value) {
			clearInterval(refreshInterval.value);
			refreshInterval.value = null;
		}
	}

	const lastUpdate = ref(null);

	async function fetchData(range, dayParams = null, isAutorefresh = false) {
		currentRange.value = range || "24h";
		emit("update:range", currentRange.value);

		const params = {
			entity: props.entity,
		};

		if (range === "custom" && dayParams) {
			// Range personalizzato
			params.range = "custom";
			params.start = dayParams.start.toISOString();
			params.end = dayParams.end ? dayParams.end.toISOString() : new Date().toISOString();

			console.log(`📊 StateChart: Range personalizzato DA ${dayParams.start.toISOString()} A ORA`);
		} else {
			// Range normale (24h, 7d, 30d)
			params.range = currentRange.value;
		}

		const res = await axios.get(`/records/devices/${props.deviceId}/states`, {
			params: params,
		});

		const { data, meta } = res.data || {};

		// Aggiorniamo l'orario dell'ultimo check andato a buon fine
		const now = new Date();
		lastUpdate.value = now.toLocaleTimeString("it-IT", {
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
		});

		// Check se il dato è recente (15 min)
		let isLive = false;
		if (data && data.length > 0) {
			const lastPt = data[data.length - 1];
			if (lastPt && lastPt.ts) {
				const lastDate = new Date(lastPt.ts);
				const threshold = 15 * 60 * 1000;
				const diff = now.getTime() - lastDate.getTime();
				isLive = diff <= threshold;
			}
		}
		isDelay.value = !isLive;

		console.log(`Grafico Stati => Dati(${currentRange.value}):`, meta?.total || 0);
		if (meta?.total) {
			console.log("Grafico Stati => Primo TS: ", data[0].ts, "; Ultimo TS: ", data[data.length - 1].ts, "; range: ", currentRange.value);
		}

		// const rows = res.data || [];
		buildChart(data, meta, isAutorefresh);
	}

	function buildChart(data = [], meta = {}, isAutoRefresh = false) {
		if (!Array.isArray(data) || !canvas.value) return;

		if (chartInstance.value) {
			chartInstance.value.destroy();
			chartInstance.value = null;
		}

		// Se il grafico esiste già e stiamo facendo un auto-refresh
		if (chartInstance.value && isAutoRefresh) {
			//! tofix: --- LOGICA SALVATAGGIO ZOOM ---
			const { min, max } = chartInstance.value.scales.x;

			chartInstance.value.data.datasets[0].data = cleaned;

			// Aggiorniamo i dati ma forziamo il mantenimento dello zoom
			chartInstance.value.update("none");

			// Riapplichiamo i limiti per evitare il reset dello zoom
			chartInstance.value.scales.x.options.min = min;
			chartInstance.value.scales.x.options.max = max;
			chartInstance.value.update("none");
			return;
		}

		const validData = data.filter((d) => d.value != null && d.value in STATE_INDEX).sort((a, b) => new Date(a.ts) - new Date(b.ts));

		if (validData.length === 0) {
			toast.info(`Nessun dato disponibile relativo a '${props.entity}' per il periodo selezionato.`, { position: "top-right", duration: 2500 });
			return;
		}

		// 🔥 CALCOLO FINE RANGE
		const rangeEnd = currentRange.value === "custom" ? selectedDay.value?.end ?? new Date() : meta?.lastTs ? new Date(meta.lastTs) : new Date(validData[validData.length - 1].ts);

		const indexed = validData.map((d) => ({
			x: isoToTimestamp(d.ts),
			y: STATE_INDEX[d.value],
		}));

		// 🔥 ESTENSIONE ULTIMO STATO
		indexed.push({
			x: rangeEnd.getTime(),
			y: indexed[indexed.length - 1].y,
		});

		categories.value = Object.keys(STATE_INDEX);

		chartInstance.value = new Chart(canvas.value, {
			type: "line",
			data: {
				datasets: [
					{
						label: props.entity,
						data: indexed,
						stepped: true,
						borderWidth: 3,
						pointRadius: 0,
						segment: {
							borderColor: (ctx) => {
								const y = ctx.p0.parsed.y;
								const state = Object.entries(STATE_INDEX).find(([, v]) => v === y)?.[0];
								return getStateColor(state);
							},
						},
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				animation: false,
				scales: {
					x: {
						...chartTimeConfig.scales.x,
						bounds: 'ticks',
						time: { 
							...chartTimeConfig.scales.x.time,
							unit: 'minute',
							stepSize: currentRange.value === '1h' ? 1 : 30,
							round: 'minute'
						},
						ticks: { 
							...chartTimeConfig.scales.x.ticks,
							autoSkip: currentRange.value === '1h' || currentRange.value === '24h' ? false : true,
							maxRotation: 45,
							minRotation: 45,
							maxTicksLimit: 100,
							callback: function(value, index, ticks) {
								const date = new Date(value);
								if (currentRange.value !== '1h' && date.getUTCMinutes() % 30 !== 0) return null;
								return chartTimeConfig.scales.x.ticks.callback(value, index, ticks);
							}
						},
						adapters: { ...chartTimeConfig.scales.x.adapters },
					},
					y: {
						ticks: {
							callback: (v) => Object.entries(STATE_INDEX).find(([, i]) => i === v)?.[0] || "",
							color: "#fff",
						},
					},
				},
				plugins: {
					zoom: {
						pan: {
							enabled: true,
							mode: "x",
							onPan: ({ chart }) => updateChartIntervals(chart),
						},
						zoom: {
							wheel: { 
								enabled: true,
								modifierKey: 'alt'
							},
							pinch: { enabled: true },
							mode: "x",
							onZoom: ({ chart }) => updateChartIntervals(chart),
						},
					},
					legend: { display: false },
					tooltip: {
						callbacks: {
							...chartTimeConfig.plugins.tooltip.callbacks,
							label: (ctx) => Object.entries(STATE_INDEX).find(([, i]) => i === ctx.raw?.y)?.[0] || "",
						},
					},
				},
				interaction: chartTimeConfig.interaction,
			},
		});

		// Applicazione iniziale degli intervalli basata sul range corrente
		updateChartIntervals(chartInstance.value, currentRange.value);
	}

	function updateChartIntervals(chart, forcedRange = null) {
		if (!chart) return;

		let rangeHours = 24; // Default fallback

		if (forcedRange && forcedRange !== "custom") {
			if (forcedRange === "1h") rangeHours = 1;
			else if (forcedRange === "24h") rangeHours = 24;
			else if (forcedRange === "7d") rangeHours = 168;
			else if (forcedRange === "30d") rangeHours = 720;
		} else {
			const scaleX = chart.scales.x;
			if (scaleX && scaleX.min !== undefined && scaleX.max !== undefined) {
				const rangeMs = scaleX.max - scaleX.min;
				rangeHours = rangeMs / (1000 * 60 * 60);
			}
		}

		let unit = "minute";
		let stepSize = 30;

		if (rangeHours <= 1.1) {
			stepSize = 1;
		} else if (rangeHours <= 4.1) {
			stepSize = 10;
		} else if (rangeHours <= 8.1) {
			stepSize = 15;
		} else {
			stepSize = 30;
		}

		if (chart.options.scales.x.time && chart.options.scales.x.ticks) {
			chart.options.scales.x.time.unit = unit;
			chart.options.scales.x.time.stepSize = stepSize;
			chart.options.scales.x.bounds = 'ticks';

			if (rangeHours <= 25.1) {
				chart.options.scales.x.ticks.autoSkip = false;
				chart.options.scales.x.ticks.maxRotation = 45;
				chart.options.scales.x.ticks.minRotation = 45;
				chart.options.scales.x.ticks.maxTicksLimit = 100;
			} else {
				chart.options.scales.x.ticks.autoSkip = true;
				chart.options.scales.x.ticks.maxRotation = 0;
				chart.options.scales.x.ticks.minRotation = 0;
			}
		}
		chart.update("none");
	}

	function openDayPicker() {
		showDayPicker.value = true;
	}

	function closeDayPicker() {
		showDayPicker.value = false;
	}

	function applyDaySelection(dayData) {
		selectedDay.value = dayData;
		emit("update:selectedDay", dayData);
		fetchData("custom", dayData);
		closeDayPicker();
	}

	watch(
		() => [props.initialRange, props.initialSelectedDay],
		([newRange, newDay]) => {
			const rangeChanged = newRange !== currentRange.value;
			const dayChanged = JSON.stringify(newDay) !== JSON.stringify(selectedDay.value);

			if (rangeChanged || dayChanged) {
				currentRange.value = newRange;
				selectedDay.value = newDay;
				fetchData(newRange, newDay);
			}
		}
	);

	watch(
		() => [props.entity, props.deviceId],
		() => {
			if (currentRange.value === "custom") {
				currentRange.value = "24h";
				selectedDay.value = null;
				emit("update:range", "24h");
				emit("update:selectedDay", null);
			}
			fetchData(currentRange.value, selectedDay.value);
		}
	);

	onMounted(() => {
		fetchData(currentRange.value, selectedDay.value);
		startPolling(); // Attiva l'aggiornamento automatico
	});

	onBeforeUnmount(() => {
		stopPolling(); // Importante: evita memory leak
		if (chartInstance.value) {
			chartInstance.value.destroy();
		}
	});
</script>

<style scoped>
	.title-group {
		display: flex;
		flex-direction: column; /* Titolo sopra, status sotto */
		align-items: flex-start;
		gap: 4px;
	}

	.live-status {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.live-indicator {
		font-size: 0.7rem;
		font-weight: bold;
		color: #4caf50;
		display: flex;
		align-items: center;
		gap: 5px;
		letter-spacing: 1px;
	}

	.last-update {
		font-size: 0.65rem;
		color: rgba(255, 255, 255, 0.5);
		font-family: monospace;
	}

	.dot {
		width: 7px;
		height: 7px;
		background-color: #4caf50;
		border-radius: 50%;
		display: inline-block;
		animation: pulse 2s infinite;
	}

	@keyframes pulse {
		0% {
			box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
		}
		70% {
			box-shadow: 0 0 0 8px rgba(76, 175, 80, 0);
		}
		100% {
			box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
		}
	}

	.live-indicator.is-delay {
		color: #ff9800;
	}

	.live-indicator.is-delay .dot {
		background-color: #ff9800;
		animation: none;
		opacity: 0.8;
	}

	.card {
		border: 1px solid #444;
		border-radius: 8px;
		padding: 16px;
		background: #1a1a1a;
		color: white;
	}

	.card-hdr {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 16px;
	}

	.card-hdr h3 {
		margin: 0;
		font-size: 1.1rem;
	}

	.card-body {
		height: 260px;
		overflow: hidden;
		position: relative;
	}

	canvas {
		width: 100% !important;
		height: 100% !important;
	}

	.range-buttons {
		display: flex;
		gap: 8px;
		margin-left: auto;
	}

	.range-buttons button {
		background: #333;
		padding: 6px 12px;
		border: 1px solid #555;
		border-radius: 4px;
		cursor: pointer;
		color: white;
		font-size: 0.85rem;
		min-width: 85px;
		text-align: center;
		transition: all 0.2s;
		white-space: nowrap;
	}

	.short-label {
		display: none;
	}

	@media (max-width: 600px) {
		.card-hdr {
			flex-direction: column;
			align-items: flex-start;
			gap: 12px;
		}

		.range-buttons {
			width: 100%;
			justify-content: flex-start;
			overflow-x: auto;
			padding-bottom: 4px;
			scrollbar-width: none;
			-ms-overflow-style: none;
		}

		.range-buttons::-webkit-scrollbar {
			display: none;
		}

		.full-label {
			display: none;
		}

		.short-label {
			display: inline;
		}

		.range-buttons button {
			padding: 6px 12px;
			min-width: fit-content;
		}
	}

	.range-buttons button:hover {
		background: #444;
	}

	.range-buttons button.active[data-range="1h"] {
		background: #e91e63;
		border-color: #e91e63;
	}

	.range-buttons button.active[data-range="24h"] {
		background: #ff9800;
		border-color: #ff9800;
	}

	.range-buttons button.active[data-range="7d"] {
		background: #2196f3;
		border-color: #2196f3;
	}

	.range-buttons button.active[data-range="30d"] {
		background: #4caf50;
		border-color: #4caf50;
	}

	.range-buttons button.active[data-range="custom"] {
		background: #9c27b0;
		border-color: #9c27b0;
	}

	.state-legend {
		margin-top: 12px;
		display: flex;
		flex-wrap: wrap;
		gap: 8px 16px;
	}

	.state-legend-item {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		font-size: 0.8rem;
		color: #ddd;
	}

	.color-dot {
		width: 12px;
		height: 12px;
		border-radius: 50%;
	}
</style>
